import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import { CoreResponseType, SubmitResponse, TFormData } from '#/client/models';
import { falsyToNull, noop, techSearchParams } from '#/service/utils';
import { authActions, useAuthStore } from '#/store/auth/auth.store';
import { modalActions } from '#/store/modal/modal.store';
import { tb } from '#/store/translation/translation.store';

interface AutoLoginToken extends TFormData {
  token: string;
}

interface AutoLogin {
  isAutoLogin: boolean;
  autoLoginToken: AutoLoginToken;
  removeAutoLoginParam: () => void;
}

const parseAutoLoginParam = (
  isAutoLogin: boolean,
  queryParam: string
): AutoLoginToken => {
  let autoLoginToken = { token: '' };

  if (isAutoLogin) {
    try {
      autoLoginToken = JSON.parse(
        // sonar complains about `escape` here. we already have ONEWEB-3320.
        decodeURIComponent(escape(window.atob(queryParam)))
      ) as AutoLoginToken;
    } catch (err) {
      console.error('parseAutoLoginParam', err);
    }
  }

  return autoLoginToken;
};

export const useInitAutoLogin = () => {
  const { isInitialised, isLoggedIn } = useAuthStore();
  const { isAutoLogin, removeAutoLoginParam } = useAutoLogin();

  if (!isInitialised || !isAutoLogin) {
    return;
  }

  if (isLoggedIn) {
    removeAutoLoginParam();
  } else {
    authActions.showLogin();
  }
};

type Submit = (data: TFormData) => Promise<SubmitResponse>;

export const useSubmitAutoLogin = (submit: Submit) => {
  const { isAutoLogin, autoLoginToken, removeAutoLoginParam } = useAutoLogin();

  return useEffect(() => {
    if (isAutoLogin) {
      submit(autoLoginToken)
        .then((res) => {
          if (res._meta.type === CoreResponseType.ERROR) {
            const message =
              falsyToNull(res._meta.message) ?? tb('error.generic.message');

            modalActions.showModal({
              buttonLabel: tb('button.ok'),
              message: message,
              onDismiss: () => authActions.hideLogin(),
            });
          }
        })
        .catch((error) => {
          console.error('Auto login failed', { error });
        })
        .then(removeAutoLoginParam)
        .catch(noop);
    }
  }, [autoLoginToken, isAutoLogin, removeAutoLoginParam, submit]);
};

export const useAutoLogin = (): AutoLogin => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  const autoLoginParam = searchParams.get('autoLogin') ?? '';

  const isAutoLogin = !!autoLoginParam;

  const autoLoginToken = parseAutoLoginParam(isAutoLogin, autoLoginParam);

  const removeAutoLoginParam = useCallback(() => {
    navigate(
      {
        pathname: pathname,
        search: techSearchParams(search),
      },
      { replace: true }
    );
  }, [navigate, pathname, search]);

  return {
    isAutoLogin,
    autoLoginToken,
    removeAutoLoginParam,
  };
};
